/**
 * Types for working-tree change payloads sent to the session UI.
 */

export type WorkingTreeChangeKind = 'added' | 'removed' | 'modified' | 'moved';

export const MAX_PATCH_CHARS = 350_000;

export interface WorkingTreeChangeFileRow {
  /** File path relative to the bridge or session/preview workspace root (wire: `workspaceRelPath`). */
  workspaceRelPath: string;
  additions: number;
  deletions: number;
  change: WorkingTreeChangeKind;
  /** Unified diff vs HEAD for the right pane (truncated when huge). */
  patchContent?: string;
  /** Total lines in the resulting file when known (from disk or git object). */
  totalLines?: number | null;
  /** Previous path relative to the workspace root when `change` is `moved`. */
  movedFromWorkspaceRelPath?: string;
  /** Previous path within the git repo when `change` is `moved`. */
  movedFromPathInRepo?: string;
}

/** One local commit not yet reachable from the resolved upstream ref. */
export interface WorkingTreeUnpushedCommitRef {
  sha: string;
  shortSha: string;
  subject: string;
  /** ISO-8601 committer date from `git log %cI`. */
  committedAt: string;
}

/** Recent commit on the current branch, with push state vs upstream / remote default. */
export interface WorkingTreeRecentCommitRef {
  sha: string;
  shortSha: string;
  subject: string;
  /** ISO-8601 committer date from `git log %cI`. */
  committedAt: string;
  /** True when this commit is not yet reachable from the resolved upstream ref. */
  needsPush: boolean;
}

export interface WorkingTreeChangeRepoDetail {
  /** Repo path relative to the bridge or session/preview workspace root (wire: `repoRelPath`). */
  repoRelPath: string;
  /** Short title, e.g. `org/repo` for GitHub remotes or last path segment. */
  repoDisplayName: string;
  branch: string;
  remoteUrl: string;
  /** Short label for tooltips, e.g. `origin · github.com/org/repo`. */
  remoteDisplay: string;
  files: WorkingTreeChangeFileRow[];
  /** Whether `git status` reports any unstaged/untracked work in this repo. */
  hasUncommittedChanges: boolean;
  /** Local commits ahead of upstream / remote default (newest first in JSON). */
  unpushedCommits: WorkingTreeUnpushedCommitRef[];
  /** Recent commits on the current branch (newest first in JSON). */
  recentCommits: WorkingTreeRecentCommitRef[];
  /** True when older commits exist beyond `recentCommits`. */
  recentCommitsHasMore: boolean;
  /** What `files` / patches represent. */
  changesView: 'working' | 'commit';
  /** Set when `changesView` is `commit`. */
  changesCommitSha: string | null;
}
